import {
  Action,
  getModule,
  Module,
  Mutation,
  VuexModule
} from "vuex-module-decorators";
import store from "@/store";
import { StiDestController } from "./StiDestController";
import { AccountController } from "./AccountController";
import { BaggingController } from "./BaggingController";
import { RequestDownloadIncomingOutgoing } from "@/domain/entities/IncomingOutgoing";
import { container } from "tsyringe";
import { IncomingOutgoingPresenter } from "../presenters/IncomingOutgoingPresenter";
import { CargoV2Presenter } from "../presenters/CargoV2Presenter";
import { MainAppController } from "./MainAppController";
import {
  CalculateDaysBetween2Dates,
  parsingErrorResponse
} from "@/app/infrastructures/misc/Utils";
import { ErrorMessageEntities } from "@/domain/entities/MainApp";
import { StiDestListRequest } from "@/data/payload/api/StiDestApiRequest";
import { BaggingListRequest } from "@/data/payload/api/BaggingApiRequest";
import { CargoV2ListRequest } from "@/data/payload/api/CargoV2ApiRequest";
import { CargoV2Entities } from "@/domain/entities/CargoV2";
import { PaginationV2 } from "@/domain/entities/Pagination";
import { getCountryScopeParam } from "@/app/infrastructures/misc/CountryScope";

export interface IncomingOutgoingState {
  isError: boolean;
  errorCause: string;
  cargoList: CargoV2Entities;
  cargoPagination: PaginationV2;
  cargoSearch: string;
  cargoLoading: boolean;
  cargoErrorCause: string;
}

@Module({ namespaced: true, dynamic: true, store, name: "incoming_outgoing" })
class IncomingOutgoingStore extends VuexModule
  implements IncomingOutgoingState {
  public isError = false;
  public errorCause = "";
  cargoList = new CargoV2Entities();
  cargoPagination = new PaginationV2();
  cargoSearch = "";
  cargoLoading = false;
  cargoErrorCause = "";
  form: any = {
    console: "",
    processName: "",
    processValue: "",
    periodStart: "",
    periodEnd: "",
    partnerId: 0,
    hubIds: ""
  };

  advanceFilterDataCargo = {
    type: [] as { name: string; value: string }[],
    cityOriginCode: "",
    cityDestinationCode: "",
    airport: ""
  };

  @Mutation
  setCargoList(payload: CargoV2Entities): void {
    this.cargoList = payload;
    this.cargoPagination = payload.pagination;
  }

  @Mutation
  setCargoPagination(payload: PaginationV2): void {
    this.cargoPagination = payload;
  }

  @Mutation
  setCargoSearch(value: string): void {
    this.cargoSearch = value;
  }

  @Mutation
  setCargoLoading(value: boolean): void {
    this.cargoLoading = value;
  }

  @Mutation
  setCargoErrorCause(value: string): void {
    this.cargoErrorCause = value;
  }

  @Mutation
  setAdvanceFilterDataCargo(value: {
    type?: { name: string; value: string }[];
    cityOriginCode?: string;
    cityDestinationCode?: string;
    airport?: string;
  }): void {
    this.advanceFilterDataCargo = { ...this.advanceFilterDataCargo, ...value };
  }

  @Action
  public fetchStiDestList() {
    StiDestController.getStiDestList(
      new StiDestListRequest({
        page: StiDestController.stiDestList.pagination.page,
        limit: StiDestController.stiDestList.pagination.limit,
        search: StiDestController.filter.search,
        startDate: this.form.periodStart,
        endDate: this.form.periodEnd,
        partnerId: this.form.partnerId,
        countryCode: getCountryScopeParam()
      })
    );
  }

  @Action
  public fetchBaggingList() {
    BaggingController.getBaggingList(
      new BaggingListRequest({
        page: BaggingController.baggingList.pagination.page,
        limit: BaggingController.baggingList.pagination.limit,
        search: BaggingController.filter.search,
        startDate: this.form.periodStart,
        endDate: this.form.periodEnd,
        partnerId: this.form.partnerId,
        countryCode: getCountryScopeParam()
      })
    );
  }

  @Action
  public async fetchCargoList(): Promise<void> {
    this.setCargoLoading(true);
    this.setCargoErrorCause("");
    const accountOrigin =
      AccountController.accountData.account_location?.city_code ?? "";
    try {
      const presenter = container.resolve(CargoV2Presenter);
      const params = new CargoV2ListRequest({
        page: this.cargoPagination.page,
        limit: this.cargoPagination.limit,
        search: this.cargoSearch,
        cargoTypes: this.advanceFilterDataCargo.type
          .map((t) => t.value)
          .join(","),
        originCity: this.advanceFilterDataCargo.cityOriginCode
          ? this.advanceFilterDataCargo.cityOriginCode
          : accountOrigin,
        destinationCity: this.advanceFilterDataCargo.cityDestinationCode,
        startDate: this.form.periodStart,
        endDate: this.form.periodEnd,
        destinationAirport: this.advanceFilterDataCargo.airport,
        partnerId: this.form.partnerId ?? 0,
        hubIds: this.form.hubIds ?? "",
        countryCode: getCountryScopeParam()
      });
      const data = await presenter.getCargoV2List(params);
      this.setCargoList(data);
    } catch (e) {
      this.setCargoErrorCause((e as Error)?.message ?? "Failed to fetch list");
    } finally {
      this.setCargoLoading(false);
    }
  }

  @Action
  public setCargoFirstPage(): void {
    this.setCargoPagination(
      new PaginationV2({ ...this.cargoPagination, page: 1 })
    );
  }

  @Action
  public getDownload(params: RequestDownloadIncomingOutgoing) {
    MainAppController.showLoading();

    if (CalculateDaysBetween2Dates(params.endDate, params.startDate) > 30) {
      MainAppController.closeLoading();
      MainAppController.showErrorMessage(
        new ErrorMessageEntities({
          title: "Gagal Download!",
          message: "Tanggal yang dipilih maksimal 30 hari",
          image: "image-modal-failed",
          buttonSuccessText: "Ubah Filter Tanggal"
        })
      );
    } else {
      const presenter = container.resolve(IncomingOutgoingPresenter);
      presenter
        .getDownload(params)
        .then(() => MainAppController.closeLoading())
        .catch((err) => {
          MainAppController.closeLoading();
          MainAppController.showErrorMessage(
            parsingErrorResponse(err, "", () => this.getDownload(params))
          );
        });
    }
  }

  @Action
  public resetForm() {
    this.setForm({
      console: "",
      search: "",
      processName: "",
      processValue: "",
      periodStart: "",
      periodEnd: "",
      partnerId: 0,
      hubIds: ""
    });
  }

  @Mutation
  public setForm(value: any) {
    this.form = value;
  }
}

export const IncomingOutgoingController = getModule(IncomingOutgoingStore);
